import React from "react";

import { IntegrationBusinessAction } from "actions";

import { CollapseHeader, FormCollapse } from "components/Form";
import { FinanceSubModalSection } from "constants/settings";
import { IntegrationTypes } from "constants/integrations";
import {
  useBoolean,
  useSectionHelper,
  useTimeoutRef,
  SectionToggleState,
} from "hooks";
import { withNamespace } from "components/Form/FormikControls";
import {
  getBusinessById,
  getDefaultBillingSettings,
  getIntegrationBusinessesByTypeByBusinessId,
} from "selectors";
import { useDispatch, useSelector } from "react-redux";
import { BankingDetailsForm } from "components/Form/Fields/BankingDetailsForm";
import AbrDetailsForm from "./AbrDetailsForm";

interface BusinessGeneralFinanceSectionProps {
  sectionHelper: SectionToggleState;
  businessId: string | null;
  namespace: string;
  abn: string;
  abrLastChecked: string;
}

export const BusinessGeneralFinanceSection: React.FC<
  BusinessGeneralFinanceSectionProps
> = ({ sectionHelper, businessId, namespace, abn, abrLastChecked }) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.GENERAL,
  );

  const header = <CollapseHeader title="General" />;

  const dispatch = useDispatch();

  const billingSettings = useSelector(getDefaultBillingSettings);
  const business = useSelector(getBusinessById(businessId));
  const xeroIntegrationBusinesses = useSelector(
    getIntegrationBusinessesByTypeByBusinessId(
      IntegrationTypes.Xero,
      businessId,
    ),
  );
  const xeroIntegrationBusiness = xeroIntegrationBusinesses[0];

  const showBpay = billingSettings && billingSettings.bpayBillerCode;
  const xeroBankingLocked = business?.xeroBankingLocked ?? false;

  const [syncNowDisabled, disableSyncNow, enableSyncNow] = useBoolean(false);
  const reenableSyncNowTimeoutRef = useTimeoutRef();

  function onSyncNow() {
    dispatch(
      IntegrationBusinessAction.synchronize(
        xeroIntegrationBusiness.id,
        xeroIntegrationBusiness.credentialsId,
      ),
    );
    disableSyncNow();
    reenableSyncNowTimeoutRef.current = setTimeout(enableSyncNow, 3000);
  }

  return (
    <FormCollapse isOpen={isOpen} onToggle={onToggle} header={header}>
      <BankingDetailsForm
        namespace={withNamespace(namespace, "bankingDetails")}
        xeroBankingLocked={xeroBankingLocked}
        onSyncNow={xeroIntegrationBusiness ? onSyncNow : undefined}
        syncNowDisabled={syncNowDisabled}
      />
      <AbrDetailsForm
        namespace={namespace}
        businessId={businessId}
        abn={abn}
        abrLastChecked={abrLastChecked}
        showBpay={showBpay}
      />
    </FormCollapse>
  );
};
